/**
 * ColumnsMenu — toolbar popover for column visibility and pinning.
 *
 * Renders one row per hideable column with:
 *   - visibility checkbox (left)
 *   - column label (center)
 *   - pin controls: pin-left / unpin / pin-right (right)
 *
 * Built on the shared `<Popover>` primitive so the look matches the
 * design-system tokens (qhs-r-xl, qhs-sh-3, qhs-surface, …) — no
 * bespoke styled-components here.
 *
 * Pinning routes through MRT's `column.pin('left'|'right'|false)`,
 * which is already wired to `_setColumnPinning` in Table.jsx and from
 * there to Redux + the server. So this component only drives state;
 * no new persistence path is introduced.
 */
import { useState } from 'react';
import { IconPinned, IconPinnedOff } from '@tabler/icons-react';
import { Popover } from '@shared/ui/Popover';
import TableToolbarButton from './TableToolbarButton';
import { ColumnsIcon, CheckIcon } from './icons';

const resolveHeader = column => {
  const def = column.columnDef;
  if (typeof def.header === 'string') return def.header;
  if (typeof def.Header === 'string') return def.Header;
  return column.id;
};

// Inline pin button — small, square, no qhs-pop__item shell. Keeps
// the row visually compact and lets us render three controls side by
// side without crowding the label.
const PinControl = ({ active, disabled, title, onClick, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    style={{
      width: 24,
      height: 24,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      border: '1px solid transparent',
      background: active ? 'rgba(46, 72, 211, 0.10)' : 'transparent',
      color: active ? 'var(--qhs-brand)' : 'var(--qhs-text-3)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'background-color 120ms ease, color 120ms ease',
      padding: 0,
    }}
    onMouseEnter={e => {
      if (disabled || active) return;
      e.currentTarget.style.background = 'var(--qhs-surface-hover)';
      e.currentTarget.style.color = 'var(--qhs-text-1)';
    }}
    onMouseLeave={e => {
      if (disabled || active) return;
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = 'var(--qhs-text-3)';
    }}
  >
    {children}
  </button>
);

const ColumnsMenu = ({
  table,
  title = 'Columns',
  label = 'Toggle columns',
  unpinAllLabel = 'Unpin all',
}) => {
  const [open, setOpen] = useState(false);

  const columns = table.getAllColumns().filter(col => col.getCanHide());

  // Any real (non-display) column currently pinned? normalizePinning keeps the
  // mrt-row-select / mrt-row-actions display columns pinned, so exclude them.
  const pinning = table.getState().columnPinning || {};
  const anyPinned =
    (pinning.left || []).some(id => id !== 'mrt-row-select') ||
    (pinning.right || []).some(id => id !== 'mrt-row-actions');

  // Bulk unpin: routes through MRT's onColumnPinningChange (= Table.jsx's
  // normalizePinning), which re-adds the display columns and persists to Redux.
  const handleUnpinAll = () => table.setColumnPinning({ left: [], right: [] });

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom-end"
      trigger={
        <TableToolbarButton
          icon={<ColumnsIcon />}
          title={title}
          active={open}
          aria-label={label}
          onClick={() => setOpen(o => !o)}
        />
      }
    >
      <div className="qhs-pop__head" style={{ display: 'flex', alignItems: 'center' }}>
        <h5 style={{ margin: 0 }}>{title}</h5>
        <button
          type="button"
          onClick={handleUnpinAll}
          disabled={!anyPinned}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: 'none',
            background: 'transparent',
            padding: '2px 4px',
            fontSize: 12,
            cursor: anyPinned ? 'pointer' : 'default',
            color: anyPinned ? 'var(--qhs-brand)' : 'var(--qhs-text-3)',
            opacity: anyPinned ? 1 : 0.5,
          }}
        >
          <IconPinnedOff size={14} />
          {unpinAllLabel}
        </button>
      </div>
      <div className="qhs-pop__body">
        {columns.map(col => {
          const visible = col.getIsVisible();
          const pinned = col.getIsPinned(); // 'left' | 'right' | false
          const canPin = col.getCanPin();
          return (
            <div
              key={col.id}
              className="qhs-pop__item"
              role="group"
              aria-label={resolveHeader(col)}
              style={{ cursor: 'default' }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={visible}
                onClick={() => col.toggleVisibility()}
                title={visible ? 'Hide column' : 'Show column'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  border: `1.5px solid ${
                    visible ? 'var(--qhs-brand)' : 'var(--qhs-border-strong)'
                  }`,
                  background: visible ? 'var(--qhs-brand)' : 'var(--qhs-surface)',
                  color: '#fff',
                  cursor: 'pointer',
                  flexShrink: 0,
                  padding: 0,
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                }}
              >
                {visible && <CheckIcon />}
              </button>
              <span
                className="qhs-pop__item-label"
                onClick={() => col.toggleVisibility()}
                style={{ cursor: 'pointer' }}
              >
                {resolveHeader(col)}
              </span>
              <span style={{ display: 'inline-flex', gap: 2, marginLeft: 16 }}>
                <PinControl
                  active={pinned === 'left'}
                  disabled={!canPin}
                  title="Pin to left"
                  onClick={() => col.pin(pinned === 'left' ? false : 'left')}
                >
                  <IconPinned size={14} style={{ transform: 'rotate(90deg)' }} />
                </PinControl>
                <PinControl
                  active={false}
                  disabled={!canPin || !pinned}
                  title="Unpin"
                  onClick={() => col.pin(false)}
                >
                  <IconPinnedOff size={14} />
                </PinControl>
                <PinControl
                  active={pinned === 'right'}
                  disabled={!canPin}
                  title="Pin to right"
                  onClick={() => col.pin(pinned === 'right' ? false : 'right')}
                >
                  <IconPinned size={14} style={{ transform: 'rotate(-90deg)' }} />
                </PinControl>
              </span>
            </div>
          );
        })}
      </div>
    </Popover>
  );
};

export default ColumnsMenu;
